import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Calendar, ArrowRight, Clock } from 'lucide-react';
import { useSafeAnimation } from '../components/ui/SafeMotion';

const SITE_URL = 'https://www.theecoenviro.com';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  image: string;
}

export function Insights() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  // SSR-safe animation props
  const heroAnim = useSafeAnimation(
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0 }
  );
  const articleAnim = useSafeAnimation(
    { opacity: 0, y: 30 },
    { opacity: 1, y: 0 }
  );
  const ctaAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );

  useEffect(() => {
    fetch('/data/insights.json')
      .then((res) => res.json())
      .then((data) => {
        setArticles(data.articles);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load insights:', err);
        setLoading(false);
      });
  }, []);

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Insights & Blog | Eco Enviro - Waste Management News & Updates</title>
        <meta name="title" content="Insights & Blog | Eco Enviro - Waste Management News & Updates" />
        <meta name="description" content="Latest insights on waste management, EPR compliance, plastic recycling, circular economy solutions, and sustainability practices from Eco Enviro experts." />
        <meta name="keywords" content="waste management blog, EPR compliance news, plastic recycling insights, circular economy India, sustainability blog, Eco Enviro insights" />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/insights`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/insights`} />
        <meta property="og:title" content="Insights & Blog | Eco Enviro" />
        <meta property="og:description" content="Latest insights on waste management, EPR compliance, plastic recycling, and circular economy solutions." />
        <meta property="og:site_name" content="Eco Enviro Circular Solutions" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@theecoenviro" />
        <meta name="twitter:title" content="Insights & Blog | Eco Enviro" />
        <meta name="twitter:description" content="Latest insights on waste management, EPR compliance, plastic recycling, and circular economy." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-eco-green-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={heroAnim.initial}
            animate={heroAnim.animate}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-eco-green-100 text-eco-green-700 text-sm font-semibold rounded-full mb-4">
              Insights
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-eco-green-900 mb-6">
              Waste Management Insights
            </h1>
            <p className="text-xl text-gray-600">
              Stay updated with the latest in circular economy, compliance, and sustainable practices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-eco-green-50 rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-eco-green-100" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-eco-green-100 rounded w-1/2" />
                    <div className="h-6 bg-eco-green-100 rounded" />
                    <div className="h-4 bg-eco-green-100 rounded w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={articleAnim.initial}
                  whileInView={articleAnim.animate}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-eco-green-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-eco-green-600 text-white text-xs font-semibold rounded-full">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(article.date).toLocaleDateString('en-IN', { 
                          day: 'numeric', 
                          month: 'short', 
                          year: 'numeric' 
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {article.readTime}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-eco-green-900 mb-2 group-hover:text-eco-green-700 transition-colors">
                      {article.title}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <Link
                      to={`/insights/${article.id}`}
                      className="inline-flex items-center gap-2 text-eco-green-600 font-semibold text-sm hover:text-eco-green-700 transition-colors"
                    >
                      Read More
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-eco-green-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={ctaAnim.initial}
            whileInView={ctaAnim.animate}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Stay Updated
            </h2>
            <p className="text-eco-green-100 mb-8">
              Get the latest insights on waste management and compliance delivered to your inbox.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-eco-green-700 font-semibold rounded-xl hover:bg-eco-green-50 transition-colors"
            >
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
